import { S3Client, PutObjectCommand, GetObjectCommand, DeleteObjectCommand, CopyObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { awsCredentials, awsServices, hasBrowserAwsCredentials } from './awsConfig';

// Lazy initialization of S3 client (created when first needed, after config is loaded)
let s3Client: S3Client | null = null;

const getS3Client = (): S3Client => {
  if (!s3Client) {
    const region = awsCredentials.region;
    if (!region || !hasBrowserAwsCredentials()) {
      throw new Error(
        'Direct S3 SDK upload requires accessKeyId and secretAccessKey in aws-config.json (local development only). Production uploads use presigned URLs from the API.'
      );
    }

    s3Client = new S3Client({
      region,
      credentials: {
        accessKeyId: awsCredentials.accessKeyId,
        secretAccessKey: awsCredentials.secretAccessKey,
      },
    });
  }
  return s3Client;
};

export const s3Service = {
  /**
   * Upload a file to S3
   */
  async uploadFile(file: File, key: string, folder: string = 'active'): Promise<string> {
    // Convert File to ArrayBuffer, then to Uint8Array for AWS SDK v3
    const arrayBuffer = await file.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    const command = new PutObjectCommand({
      Bucket: awsServices.s3Bucket,
      Key: `${folder}/${key}`,
      Body: uint8Array,
      ContentType: file.type || 'application/octet-stream',
      ServerSideEncryption: 'AES256', // Required for GDPR/HIPAA compliance
    });

    try {
      await getS3Client().send(command);
      // Return the S3 URL
      return `https://${awsServices.s3Bucket}.s3.${awsCredentials.region}.amazonaws.com/${folder}/${key}`;
    } catch (error) {
      console.error('S3 upload error:', error);
      throw new Error(`Failed to upload file to S3: ${error}`);
    }
  },

  /**
   * Get a presigned URL for downloading/viewing a file
   */
  async getPresignedUrl(key: string, folder: string = 'active', expiresIn: number = 3600): Promise<string> {
    const command = new GetObjectCommand({
      Bucket: awsServices.s3Bucket,
      Key: `${folder}/${key}`,
    });

    try {
      return await getSignedUrl(getS3Client(), command, { expiresIn });
    } catch (error) {
      console.error('S3 presigned URL error:', error);
      throw new Error(`Failed to generate presigned URL: ${error}`);
    }
  },

  /**
   * Delete a file from S3
   */
  async deleteFile(key: string, folder: string = 'active'): Promise<void> {
    const command = new DeleteObjectCommand({
      Bucket: awsServices.s3Bucket,
      Key: `${folder}/${key}`,
    });

    try {
      await getS3Client().send(command);
    } catch (error) {
      console.error('S3 delete error:', error);
      throw new Error(`Failed to delete file from S3: ${error}`);
    }
  },

  /**
   * Move a file between folders (copy + delete)
   */
  async moveFile(key: string, fromFolder: string, toFolder: string): Promise<void> {
    const copyCommand = new CopyObjectCommand({
      Bucket: awsServices.s3Bucket,
      CopySource: `${awsServices.s3Bucket}/${fromFolder}/${key}`,
      Key: `${toFolder}/${key}`,
      ServerSideEncryption: 'AES256',
    });

    try {
      // Copy to new location
      await getS3Client().send(copyCommand);
      // Delete from old location
      await this.deleteFile(key, fromFolder);
    } catch (error) {
      console.error('S3 move error:', error);
      throw new Error(`Failed to move file in S3: ${error}`);
    }
  },
};
